// AdminTenants — tenant list + status pill + create/suspend/reactivate/delete actions.

import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

const APEX = import.meta.env.VITE_CHITOS_APEX_DOMAIN || 'chitdesk.com';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  tenant_urls: string[];
  status:
    | 'pending_payment'
    | 'provisioning'
    | 'active'
    | 'past_due'
    | 'suspended'
    | 'provisioning_failed'
    | 'archived';
  plan_tier: string;
  primary_phone: string | null;
  primary_email: string;
  region: string;
  created_at: Date;
  updated_at: Date;
}

const STATUS_COLOR: Record<string, string> = {
  active: 'bg-good-soft text-good',
  provisioning: 'bg-warn-soft text-warn',
  pending_payment: 'bg-warn-soft text-warn',
  past_due: 'bg-bad-soft text-bad',
  suspended: 'bg-bad-soft text-bad',
  provisioning_failed: 'bg-bad-soft text-bad',
  archived: 'bg-bg-elev text-ink/60',
};

export function AdminTenants(): ReactElement {
  const qc = useQueryClient();
  const [status_filter, set_status_filter] = useState<Tenant['status'] | 'all'>('all');
  const q = useQuery({
    queryKey: ['admin-tenants', status_filter],
    queryFn: () =>
      orpc.admin.list_tenants({
        ...(status_filter === 'all' ? {} : { status: status_filter }),
        limit: 100,
      }),
  });

  const suspend_m = useMutation({
    mutationFn: (tenant_id: string) =>
      orpc.admin.suspend_tenant({ tenant_id, reason: 'Suspended via admin console' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenants'] }),
  });
  const reactivate_m = useMutation({
    mutationFn: (tenant_id: string) => orpc.admin.reactivate_tenant({ tenant_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenants'] }),
  });
  const delete_m = useMutation({
    mutationFn: (tenant_id: string) =>
      orpc.admin.delete_tenant({ tenant_id, confirm: true as const }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenants'] }),
  });

  const [show_create, set_show_create] = useState(false);

  const tenants = (q.data?.tenants ?? []) as Tenant[];

  const handle_delete = (t: Tenant): void => {
    const ok = window.confirm(
      `Permanently delete tenant "${t.name}" (${t.subdomain})?\n\n` +
        `This drops the tenant's Mongo DB, removes the <sub>-api A record, and ` +
        `detaches <sub>.chitdesk.com from Cloudflare Pages. Cannot be undone.`,
    );
    if (!ok) return;
    delete_m.mutate(t.id);
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium">Tenants</h1>
          <p className="text-xs text-ink/60 mt-1">
            Lifecycle controls for all ChitDesk operator tenants.
          </p>
        </div>
        <button
          type="button"
          onClick={() => set_show_create(true)}
          className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded text-sm font-medium"
        >
          + New tenant
        </button>
      </div>

      {show_create && (
        <CreateTenantModal
          on_close={() => set_show_create(false)}
          on_created={() => {
            set_show_create(false);
            void qc.invalidateQueries({ queryKey: ['admin-tenants'] });
          }}
        />
      )}

      <div className="flex flex-wrap gap-1 mb-4">
        {(
          [
            'all',
            'pending_payment',
            'provisioning',
            'active',
            'past_due',
            'suspended',
            'provisioning_failed',
            'archived',
          ] as const
        ).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => set_status_filter(s)}
            className={`px-3 py-1 rounded text-xs border ${
              status_filter === s
                ? 'bg-primary-soft border-primary text-primary-ink'
                : 'bg-surface border-line text-ink/60 hover:bg-bg-elev'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {q.isLoading && <div className="text-sm text-ink/60">Loading…</div>}
      {q.error && <div className="text-sm text-bad">{friendly_error(q.error)}</div>}
      {!q.isLoading && tenants.length === 0 && (
        <div className="bg-surface border border-line rounded-lg p-8 text-center text-sm text-ink/60">
          No tenants match this filter.
        </div>
      )}
      {tenants.length > 0 && (
        <div className="bg-surface border border-line rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-elev border-b border-line text-ink/60 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Tenant</th>
                <th className="text-left px-4 py-2 font-medium">Plan</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} className="border-b border-line/40 last:border-b-0">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/tenants/${t.id}`}
                      className="font-medium hover:text-primary"
                    >
                      {t.name}
                    </Link>
                    <div className="text-[11px] text-ink/50 font-mono">
                      {t.subdomain}
                      {t.primary_phone ? ` · ${t.primary_phone}` : ''}
                      {t.primary_email ? ` · ${t.primary_email}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{t.plan_tier}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                        STATUS_COLOR[t.status] ?? 'bg-bg-elev text-ink/60'
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink/60">
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {t.status === 'active' && (
                        <button
                          type="button"
                          onClick={() => suspend_m.mutate(t.id)}
                          disabled={suspend_m.isPending}
                          className="text-xs px-2 py-1 border border-bad/30 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
                        >
                          Suspend
                        </button>
                      )}
                      {t.status === 'suspended' && (
                        <button
                          type="button"
                          onClick={() => reactivate_m.mutate(t.id)}
                          disabled={reactivate_m.isPending}
                          className="text-xs px-2 py-1 border border-good/30 text-good rounded hover:bg-good-soft disabled:opacity-50"
                        >
                          Reactivate
                        </button>
                      )}
                      {t.subdomain !== 'chitos' && (
                        <button
                          type="button"
                          onClick={() => handle_delete(t)}
                          disabled={delete_m.isPending}
                          title="Permanently delete tenant + Cloudflare entries"
                          className="text-xs px-2 py-1 border border-bad/40 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Create-tenant modal ────────────────────────────────────────────────────

interface CreateTenantModalProps {
  on_close: () => void;
  on_created: () => void;
}

function CreateTenantModal({ on_close, on_created }: CreateTenantModalProps): ReactElement {
  const [business_name, set_business_name] = useState('');
  const [subdomain, set_subdomain] = useState('');
  const [email, set_email] = useState('');
  const [phone, set_phone] = useState('');
  const [plan_tier, set_plan_tier] = useState<'starter' | 'growth' | 'scale'>('starter');
  const [sub_check, set_sub_check] = useState<{ ok: boolean; reason?: string } | null>(null);
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [success_message, set_success_message] = useState<string | null>(null);

  // Live subdomain check
  useEffect(() => {
    if (!subdomain || subdomain.length < 3) {
      set_sub_check(null);
      return;
    }
    const handle = setTimeout(() => {
      orpc.platform
        .check_subdomain({ subdomain })
        .then((r) => set_sub_check(r))
        .catch(() => set_sub_check(null));
    }, 400);
    return () => clearTimeout(handle);
  }, [subdomain]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    set_error(null);
    if (sub_check && !sub_check.ok) {
      set_error(`Subdomain ${sub_check.reason ?? 'unavailable'}`);
      return;
    }
    set_busy(true);
    try {
      const r = await orpc.admin.create_tenant({
        business_name,
        subdomain,
        primary_email: email,
        ...(phone ? { primary_phone: phone } : {}),
        plan_tier,
      });
      set_success_message(
        `Tenant "${business_name}" created. Welcome link sent to ${email}. ` +
          `Provisioning continues in the background (DNS + Pages take ~30-90s).`,
      );
      // Give the list a moment to pick up the new row, then close.
      setTimeout(() => {
        on_created();
        void r;
      }, 1_200);
    } catch (err) {
      set_error(friendly_error(err));
    } finally {
      set_busy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) on_close();
      }}
    >
      <div className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium">Create tenant</h2>
            <p className="text-xs text-ink/60 mt-1">
              Same flow as public signup, payment skipped. A welcome email with a
              set-password link is sent automatically once provisioning completes.
            </p>
          </div>
          <button
            type="button"
            onClick={on_close}
            disabled={busy}
            className="text-ink/50 hover:text-ink text-lg leading-none p-1"
          >
            ×
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="text-sm">
            <span className="block mb-1">Business name</span>
            <input
              type="text"
              value={business_name}
              onChange={(e) => set_business_name(e.target.value)}
              required
              minLength={2}
              maxLength={120}
              autoFocus
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Subdomain</span>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={subdomain}
                onChange={(e) =>
                  set_subdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                }
                required
                minLength={3}
                maxLength={30}
                pattern="[a-z][-a-z0-9]*[a-z0-9]"
                className="flex-1 px-3 py-2 border border-line rounded bg-bg-elev font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <span className="text-xs text-ink/50 font-mono">.{APEX}</span>
            </div>
            {sub_check && (
              <span
                className={`text-xs mt-1 block ${sub_check.ok ? 'text-good' : 'text-bad'}`}
              >
                {sub_check.ok ? '✓ available' : `✗ ${sub_check.reason ?? 'unavailable'}`}
              </span>
            )}
          </label>

          <label className="text-sm">
            <span className="block mb-1">
              Email <span className="text-ink/40">· welcome link goes here</span>
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => set_email(e.target.value)}
              required
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">
              Phone <span className="text-ink/40">(optional)</span>
            </span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => set_phone(e.target.value)}
              placeholder="+91 9876543210"
              pattern="\+91[6-9]\d{9}"
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Plan</span>
            <select
              value={plan_tier}
              onChange={(e) => set_plan_tier(e.target.value as 'starter' | 'growth' | 'scale')}
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="scale">Scale</option>
            </select>
          </label>

          {error && (
            <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>
          )}
          {success_message && (
            <div className="px-3 py-2 bg-good-soft text-good text-xs rounded">
              {success_message}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={on_close}
              disabled={busy}
              className="text-sm px-3 py-2 text-ink/60 hover:text-ink rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || (sub_check !== null && !sub_check.ok)}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white text-sm px-4 py-2 rounded font-medium"
            >
              {busy ? 'Creating…' : 'Create tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
